import React, { useCallback, useEffect, useState } from 'react';

import { Text } from '@mantine/core';
import Table from 'components/Table';
import useAthletesOnHold from 'hooks/reports/athletes/useAthletesOnHold';
import { useNavigate } from 'react-router-dom';
import { CellProps } from 'react-table';
import { BaseMember, HoldReport } from 'types';

import { SearchFilters } from '../types';

const AthletesOnHoldTable = ({
  searchFilters,
  setOrderingValue,
}: {
  searchFilters: SearchFilters;
  setOrderingValue: React.Dispatch<React.SetStateAction<string>>;
}): JSX.Element => {
  const navigate = useNavigate();

  const [tableData, setTableData] = useState<HoldReport[]>([]);
  const [ordering, setOrdering] = useState('athlete_package__athlete__first_name');
  const [ascending, setAscending] = useState(true);

  const { data, fetchNextPage, hasNextPage, isSuccess, isError, isLoading, isFetchingNextPage } =
    useAthletesOnHold({
      ordering: ascending ? ordering : `-${ordering}`,
      date: searchFilters.date.toISOString(),
    });

  const handleHeaderClick = (header: string): void => {
    setOrdering(header);
    setAscending(a => {
      setOrderingValue(!a ? header : `-${header}`);
      return !a;
    });
  };

  const handleNameClick = useCallback(
    (athlete: BaseMember): void => {
      navigate(`/users/athletes/${athlete.athleteId}/general-info`);
    },
    [navigate],
  );

  useEffect(() => {
    if (!isLoading && data) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [isLoading, data, setTableData]);

  const columns = [
    {
      Header: 'Name',
      accessor: 'athlete_package__athlete__first_name',
      Cell: ({ row }: CellProps<HoldReport>) => (
        <Text
          weight='bold'
          underline
          sx={{ cursor: 'pointer', width: 'fit-content' }}
          onClick={() => handleNameClick(row.original.athletePackage.athlete)}
        >
          {row.original.athletePackage.athlete.fullName}
        </Text>
      ),
    },
    {
      Header: 'Email',
      Cell: ({ row }: CellProps<HoldReport>) => <>{row.original.athletePackage.athlete.email}</>,
      disableSortBy: true,
    },
    {
      Header: 'Phone',
      Cell: ({ row }: CellProps<HoldReport>) => <>{row.original.athletePackage.athlete.phone}</>,
      disableSortBy: true,
    },
    {
      Header: 'Package',
      Cell: ({ row }: CellProps<HoldReport>) => <>{row.original.athletePackage.package.name}</>,
      disableSortBy: true,
    },
    {
      Header: 'Hold Reason',
      accessor: 'reason' as const,
    },
    {
      Header: 'Hold Start Date',
      accessor: 'startDate' as const,
      Cell: ({ value }: { value: string }) => new Date(value).toLocaleDateString(),
    },
    {
      Header: 'Hold End Date',
      accessor: 'endDate' as const,
      Cell: ({ value }: { value: string }) => new Date(value).toLocaleDateString(),
    },
  ];

  return (
    <>
      <Table
        tableData={tableData}
        columns={columns}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={!!hasNextPage}
        fetchNextPage={fetchNextPage}
        clickableHeaders={['athlete_package__athlete__first_name', 'reason', 'startDate', 'endDate']}
        handleHeaderClick={handleHeaderClick}
        ordering={ordering}
        ascending={ascending}
      />
    </>
  );
};

export default AthletesOnHoldTable;
